import React, { useEffect } from 'react';
import App from 'next/app';
import dynamic from 'next/dynamic';
import type { AppProps, AppContext } from 'next/app';
import { NextApiRequest, NextApiResponse } from 'next';
import Cors from 'cors';
import 'simplebar/dist/simplebar.min.css';
import '../styles/globals.css';
import '../styles/simplebar.css';
import Cookies from 'cookies';
import cookie from 'cookie';
import BugsnagClient, { bugsnagStart } from '../util/bugsnag';
import getGeolocation from '../util/getGeolocation';
import initMiddleware from '../util/initMiddleware';
import setTargetCookies from '../util/setTargetCookies';
import { AppData, Country } from '../util/types';
import PAGES_ID from '../util/pages_ids';
import { getEntries, getAvailableCountries } from '../contentful/client';
import { useRouter } from 'next/router';

const NextNProgress = dynamic(() => import('nextjs-progressbar'), { ssr: false });
const LocaleError = dynamic(() => import('../pageComponents/LocaleError/LocaleError'));
const Meta = dynamic(() => import('../components/Meta/Meta'));
const Navigation = dynamic(() => import('../components/Navigation/Navigation'));
const Footer = dynamic(() => import('../components/Footer/Footer'));
const BottomBlock = dynamic(() => import('../components/BottomBlock/BottomBlock'));

const cors = initMiddleware(
  Cors({
    methods: ['GET', 'POST', 'OPTIONS'],
    origin: '*',
    exposedHeaders: '*',
    credentials: true,
    optionsSuccessStatus: 200,
  }),
);

interface LocatorAppProps extends AppProps {
  data: AppData,
  startCountry?: string,
  bugsnagApiKey?: string,
  baseURL?: string,
  entries?: any,
  locale?: string,
  countryCode?: string,
  shortLocale?: string,
  availableCountries?: Country[],
}

function LocatorApp({
  Component,
  pageProps,
  data,
  bugsnagApiKey,
  startCountry,
  baseURL,
  availableCountries,
  locale,
  countryCode,
  shortLocale,
}: LocatorAppProps) {
  const router = useRouter();

  const getBugsnagBoundary = () => {
    if (bugsnagApiKey) {
      if (BugsnagClient) {
        return BugsnagClient?.getPlugin('react')?.createErrorBoundary(React);
      } else {
        bugsnagStart(bugsnagApiKey);
        //@ts-ignore
        return BugsnagClient?.getPlugin('react')?.createErrorBoundary(React);
      }
    }
    return null;
  };


  async function registerServiceWorker() {
    navigator.serviceWorker.register('/sw.js');
  }

  useEffect(() => {
    if (document) {
      document.documentElement.setAttribute('lang', shortLocale || 'en');
    }

    if (window) {
      window.addEventListener('load', registerServiceWorker);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        window.removeEventListener('load', registerServiceWorker);
      }
    };
  }, []);

  return data.status && data.message ? (
    <div style={{ minHeight: '100vh' }}>
      <NextNProgress />
      <LocaleError error={data} availableCountries={availableCountries} />
    </div>
  ) : (
    <div style={{ minHeight: '100vh' }}>
      <noscript
        dangerouslySetInnerHTML={{
          __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-WDZD3B9"
height="0" width="0" style="display:none;visibility:hidden"/>`,
        }}
      />
      <NextNProgress />
      <Meta {...data.meta} baseURL={baseURL} locale={locale} pathname={router.pathname} />
      <Navigation navigation={data?.navigation?.fields} />
      <Component {...pageProps} startCountry={startCountry} />
      {data.help && !router.pathname.includes('test-page') && <BottomBlock data={data.help.fields} />}
      <Footer footer={data?.footer?.fields} country_list={availableCountries || []} countryCode={countryCode} />
    </div>
  );
  //
  // if (ErrorBoundary) {
  //   return (
  //     <ErrorBoundary>
  //       {content}
  //     </ErrorBoundary>
  //   );
  // } else {
  //   return content;
  // }
}


LocatorApp.getInitialProps = async (appContext: AppContext) => {
  const appProps: AppProps = await App.getInitialProps(appContext) as AppProps;
  const { res, req } = appContext.ctx;

  const cookies = req && res ? new Cookies(req, res) : undefined;
  const parsedCookies = cookie.parse(req ? req.headers.cookie || '' : document.cookie);

  let ip: string = '';
  if (req) {
    if (req.headers['x-forwarded-for']) {
      ip = typeof req.headers['x-forwarded-for'] === 'string' ? req.headers['x-forwarded-for'] : req.headers['x-forwarded-for'][0];
    }
    if (!ip || ip.length === 0) {
      ip = req.connection?.remoteAddress || '';
    }
    req.headers['cache-control'] = 'max-age=86400';
    req.headers['expires'] = (new Date(Date.now() + 86400)).toString().slice(0, 33);
  }


  const {
    shortLocale,
    locale,
    country,
    region,
    // browserLanguages,
  } = await getGeolocation({
    res,
    req,
    ip,
    cookies: parsedCookies,
  });

  const data = await getEntries(PAGES_ID.app, locale);

  const availableCountries = await getAvailableCountries(locale) as { key: string, countries: Country[] };

  await setTargetCookies({
    country,
    locale,
    region,
    cookies,
  });

  await cors(appContext.ctx.req as NextApiRequest, appContext.ctx.res as NextApiResponse);

  return {
    ...appProps,
    data,
    locale,
    shortLocale,
    countryCode: country,
    availableCountries: availableCountries?.countries,
    startCountry: country,
    bugsnagApiKey: process.env.BUGSNAG_API_KEY,
    baseURL: process.env.SITE_URL,
  };
};


export default LocatorApp;



